<template>
  <b-modal
    v-model="show"
    centered
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
    size="condition-editor"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
      >
        <h5 class="modal-title">
          {{ formTitle }}
        </h5>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity: 1 !important"
          @click="close"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div class="text-right mt-1 mr-2">
        <b-button @click="close" variant="secondary">
          Close
        </b-button>
        <b-button class="ml-2" @click="submitRule" variant="primary">
          Save
        </b-button>
      </div>
    </template>

    <div class="subtitle mt-2"><b>Conditions:</b></div>
    <div>
      <b-form-group
        v-for="(statement, statementIndex) in condition.statements"
        :key="statementIndex"
        class="my-2"
      >
        <div class="d-flex align-items-center">
          <span class="">if</span>
          <b-form-select
            class="ml-2"
            v-model="statement.field"
            size="sm"
            style="min-width: 120px; max-width: 120px"
            @change="() => {statement.value = ''}"
          >
            <option value="payor">Payor</option>
            <option value="credentials">Credentials</option>
            <option value="appStatus">Appointment</option>
            <option value="supervisorCredentials"
              >Supervisor Credentials</option
            >
          </b-form-select>
          <span class="mx-2">=</span>
          <v-select
            v-model="statement.value"
            :options="
              statement.field != '' ? getValueItems(statement.field) : []
            "
            size="sm"
            style="min-width: 285px; max-width: 285px"
            class="ml-2"
            :reduce="value => value.value"
          />
          <i
            class="fas fa-trash-alt ml-3 text-danger hover-pointer"
            @click="removeStatement(statementIndex)"
          ></i>
          <i
            class="fas fa-plus ml-3 text-primary hover-pointer"
            @click="addStatement(statementIndex)"
          ></i>
        </div>
        <b-form-select
          v-if="statement.logicalOperator"
          v-model="statement.logicalOperator"
          class="mt-2"
          size="sm"
          style="min-width: 100px; max-width: 100px"
        >
          <option
            v-for="(operator, index) in logicOperators"
            :key="index"
            :value="operator.value"
          >
            {{ operator.label }}
          </option>
        </b-form-select>
      </b-form-group>
    </div>

    <div class="subtitle mt-2"><b>Fee and codes:</b></div>
    <b-row class="d-flex align-items-center mt-2">
      <b-col cols="6">
        <b-form-group
          :state="formErrors.costVariation.length == 0 ? null : false"
          :invalid-feedback="formErrors.costVariation.join('. ')"
        >
          <template v-slot:label>
            <span class="label">Cost per Unit</span>
          </template>
          <b-input-group size="sm" prepend="$">
            <b-form-input
              v-model="condition.costVariation"
              type="number"
              min="0"
              trim
            />
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group>
          <template v-slot:label>
            <span class="label">Billing Code</span>
          </template>
          <b-form-input
            v-model="condition.billingCode"
            trim
            maxlength="50"
            size="sm"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="3"
        ><b-form-group>
          <template v-slot:label>
            <span class="label">Modifier 1</span>
          </template>
          <b-form-input
            v-model="condition.mod1"
            autocomplete="off"
            trim
            size="sm"
          />
        </b-form-group>
      </b-col>
      <b-col cols="3"
        ><b-form-group>
          <template v-slot:label>
            <span class="label">Modifier 2</span>
          </template>
          <b-form-input
            v-model="condition.mod2"
            autocomplete="off"
            trim
            size="sm"
          />
        </b-form-group>
      </b-col>
      <b-col cols="3"
        ><b-form-group>
          <template v-slot:label>
            <span class="label">Modifier 3</span>
          </template>
          <b-form-input
            v-model="condition.mod3"
            autocomplete="off"
            trim
            size="sm"
          />
        </b-form-group>
      </b-col>
      <b-col cols="3"
        ><b-form-group>
          <template v-slot:label>
            <span class="label">Modifier 4</span>
          </template>
          <b-form-input
            v-model="condition.mod4"
            autocomplete="off"
            trim
            size="sm"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <span class="text-danger mt-2" v-if="condition.error.active">
      {{ condition.error.message }}
    </span>
  </b-modal>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "ServiceBillingCodeCostVariationAddEditModal",
  props: [
    "show",
    "formTitle",
    "payors",
    "credentials",
    "scheduleEventStatuses",
    "service",
    "currentCondition"
  ],
  data() {
    return {
      condition: {
        costVariation: 0,
        statements: [
          {
            field: "",
            logicalOperator: "",
            value: ""
          }
        ],
        error: {
          active: false
        }
      },
      formErrors: {
        costVariation: []
      },
      fields: [
        {
          label: "Payor",
          value: "payor"
        },
        {
          label: "Credentials",
          value: "credentials"
        },
        {
          label: "App Status",
          value: "appStatus"
        },
        {
          label: "Supervisor Credentials",
          value: "supervisorCredentials"
        }
      ],
      logicOperators: [
        {
          label: "Or",
          value: "or"
        },
        {
          label: "And",
          value: "and"
        }
      ]
    };
  },
  methods: {
    submitRule() {
      if (this.isValid() && !this.currentCondition) {
        this.$emit("onAddCondition", this.condition);
        this.resetCondition();
        return;
      }
      if (this.isValid() && this.currentCondition) {
        this.$emit("onEditCondition", {
          condition: this.condition,
          index: this.currentCondition.index
        });
        this.resetCondition();
        return;
      }
    },
    getValueItems(option) {
      switch (option) {
        case "payor":
          return this.payors.records.map(item => ({
            value: item.id,
            label: item.name
          }));
        case "credentials":
          return this.credentials.map(item => ({
            value: item.id,
            label: item.name
          }));
        case "supervisorCredentials":
          return this.credentials.map(item => ({
            value: item.id,
            label: item.name
          }));
        default:
          return this.scheduleEventStatuses.map(status => ({
            label: status.name,
            value: status.id
          }));
      }
    },
    hasEmptyStatement() {
      return this.condition.statements.some(
        statement => !statement.field || !statement.value
      );
    },
    isValidCostVariation() {
      return (
        this.condition.costVariation >= 0 ||
        parseFloat(this.condition.costVariation) >= 0
      );
    },
    isValid() {
      let isValid = true;
      this.formErrors.costVariation = [];
      this.condition.error.active = false;
      if (this.hasEmptyStatement()) {
        this.condition.error = {
          active: true,
          message: "You must fill the conditions selectors"
        };
        isValid = false;
      }

      if (!this.isValidCostVariation()) {
        this.formErrors.costVariation.push(
          "Cost per unit must be greater or equal than 0"
        );
        isValid = false;
      }

      return isValid;
    },
    addStatement(statementIndex) {
      this.condition.statements = [
        ...this.condition.statements,
        {
          field: "",
          logicalOperator: "",
          value: ""
        }
      ];
      const targetStatement = this.condition.statements[statementIndex];
      targetStatement.logicalOperator = "and";
    },
    removeStatement(statementIndex) {
      this.condition.statements.splice(statementIndex, 1);
      if (this.condition.statements.length === 0) {
        this.resetCondition();
      }
      if (statementIndex !== 0) {
        this.condition.statements[statementIndex - 1].logicalOperator = "";
      }
    },
    resetCondition() {
      this.condition = {
        costVariation: 0,
        statements: [
          {
            field: "",
            logicalOperator: "",
            value: ""
          }
        ],
        error: {
          active: false
        }
      };
      this.formErrors.costVariation = [];
    },
    close() {
      if (this.currentCondition) {
        this.condition.costVariation = this.costPerUnit;
        this.condition.billingCode = this.billingCode;
        this.condition.mod1 = this.mod1;
        this.condition.mod2 = this.mod2;
        this.condition.mod3 = this.mod3;
        this.condition.mod4 = this.mod4;
        this.condition.statements =  JSON.parse(JSON.stringify(this.statements));
        this.condition.error.active = false;
        this.$emit("onHiddenEdit", {
          condition: this.condition,
          index: this.currentCondition.index
        });
      } else {
        this.$emit("onHiddenAdd");
      }
      this.resetCondition();
    },
    persistCurrentConditionFieldbyField() {
      this.costPerUnit = this.currentCondition.condition.costVariation;
      this.billingCode = this.currentCondition.condition.billingCode;
      this.mod1 = this.currentCondition.condition.mod1;
      this.mod2 = this.currentCondition.condition.mod2;
      this.mod3 = this.currentCondition.condition.mod3;
      this.mod4 = this.currentCondition.condition.mod4;
      this.statements =  JSON.parse(JSON.stringify(this.currentCondition.condition.statements));
    }
  },
  watch: {
    show: {
      handler: function(value) {
        if (value) {
          if (this.currentCondition) {
            this.persistCurrentConditionFieldbyField();
            this.condition = this.currentCondition.condition;
            this.formErrors.costVariation = [];
          }
          if (this.service && !this.currentCondition) {
            this.condition.costVariation = this.service.costPerUnit;
            this.condition.billingCode = this.service.billingCode;
            this.condition.mod1 = this.service.mod1;
            this.condition.mod2 = this.service.mod2;
            this.condition.mod3 = this.service.mod3;
            this.condition.mod4 = this.service.mod4;
          }
        }
      }
    }
  }
});
</script>

<style scoped>
.subtitle {
  font-size: 15px;
}
.label {
  font-size: 13px;
}
.modal-title {
  font-weight: bold;
}
.outer-container {
  overflow: auto;
  max-height: 500px;
}
</style>

<style>
.modal .modal-condition-editor {
  max-width: 555px;
}
</style>
